/**
 * AlertCenter - Deduplicated alerts with occurrence tracking
 *
 * Modular component that can be placed in any page.
 * Displays alerts with smart deduplication (similar alerts grouped together).
 * Updated to match WES Hub 360 Figma design.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Alert as MuiAlert,
  Skeleton,
  useTheme
} from '@mui/material';
import {
  Refresh,
  FilterList,
  Error as ErrorIcon,
  Warning,
  Info,
  MoreVert,
  KeyboardArrowDown
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import insightsService from '../../services/insightsService';

const severityConfig = {
  critical: { color: '#DC5F5F', icon: <ErrorIcon />, priority: 1 },
  warning: { color: '#E27D35', icon: <Warning />, priority: 2 },
  info: { color: '#016FF4', icon: <Info />, priority: 3 }
};

const AlertCenter = ({ maxItems = 10, autoRefresh = true, panelLayout = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t, i18n } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [filter, setFilter] = useState({ status: 'open', severity: '' });
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const locale = i18n.resolvedLanguage || i18n.language || undefined;

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return t('time.justNow');
    }
    if (diffMins < 60) {
      return t('time.minutesAgo', { count: diffMins });
    }
    if (diffHours < 24) {
      return t('time.hoursAgo', { count: diffHours });
    }
    return t('time.daysAgo', { count: diffDays });
  };

  const formatDateTime = (value) => (
    new Intl.DateTimeFormat(locale, {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(value))
  );

  const getSeverityLabel = (severity) => t(`alerts.${severity}`);

  const getSeverityCountLabel = (severity, count) => {
    if (severity === 'critical') {
      return `${count} ${t(count > 1 ? 'alerts.criticalPlural' : 'alerts.critical')}`;
    }

    if (severity === 'warning') {
      return `${count} ${t(count > 1 ? 'alerts.warningPlural' : 'alerts.warning')}`;
    }

    return `${count} ${t('alerts.info')}`;
  };

  const getMockAlerts = useCallback(() => [
    {
      alert_id: 'mock-alert-1',
      type: 'execution_failure',
      title: t('alerts.mock.schedulingConflicts.title'),
      description: t('alerts.mock.schedulingConflicts.description'),
      severity: 'critical',
      occurrence_count: 2,
      first_occurrence: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      last_occurrence: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
      status: 'open'
    },
    {
      alert_id: 'mock-alert-2',
      type: 'executor_health',
      title: t('alerts.mock.executorHealth.title'),
      description: t('alerts.mock.executorHealth.description'),
      severity: 'warning',
      occurrence_count: 1,
      first_occurrence: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      last_occurrence: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
      status: 'open'
    },
    {
      alert_id: 'mock-alert-3',
      type: 'schedule_missed',
      title: t('alerts.mock.scheduleMissed.title'),
      description: t('alerts.mock.scheduleMissed.description'),
      severity: 'warning',
      occurrence_count: 1,
      first_occurrence: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      last_occurrence: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      status: 'open'
    }
  ], [t]);

  const loadAlerts = useCallback(async () => {
    try {
      setError(null);
      const params = { ...filter, limit: maxItems };
      if (!params.severity) {
        delete params.severity;
      }
      const data = await insightsService.getAlerts(params);
      setAlerts(data.alerts || []);
    } catch (err) {
      console.error('Failed to load alerts:', err);
      setAlerts(getMockAlerts());
    } finally {
      setLoading(false);
    }
  }, [filter, getMockAlerts, maxItems]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    if (!autoRefresh) {
      return undefined;
    }

    const interval = setInterval(loadAlerts, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadAlerts]);

  const handleAcknowledge = async (alertId, event) => {
    if (event) {
      event.stopPropagation();
    }

    try {
      await insightsService.acknowledgeAlert(alertId);
      loadAlerts();
      if (selectedAlert?.alert_id === alertId) {
        setSelectedAlert(null);
      }
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  const criticalCount = alerts.filter((alert) => alert.severity === 'critical').length;
  const warningCount = alerts.filter((alert) => alert.severity === 'warning').length;
  const totalOpen = alerts.filter((alert) => alert.status === 'open').length;

  const alertDialog = (
    <Dialog open={!!selectedAlert} onClose={() => setSelectedAlert(null)} maxWidth="xs" fullWidth>
      {selectedAlert && (
        <>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {severityConfig[selectedAlert.severity]?.icon}
              <Typography variant="h6" sx={{ flex: 1 }}>
                {selectedAlert.title}
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: 0.5,
                  bgcolor: severityConfig[selectedAlert.severity]?.color,
                  borderRadius: '4px'
                }}
              >
                <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                  {getSeverityLabel(selectedAlert.severity)}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedAlert.description}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              {t('alerts.occurrences')}: {selectedAlert.occurrence_count || 1}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('alerts.firstSeen')}: {formatDateTime(selectedAlert.first_occurrence)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('alerts.lastOccurrence')}: {formatDateTime(selectedAlert.last_occurrence)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedAlert(null)}>{t('common.close')}</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={(event) => handleAcknowledge(selectedAlert.alert_id, event)}
            >
              {t('alerts.acknowledge')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  if (loading) {
    if (panelLayout) {
      return (
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title">
              <h3>{t('alerts.title')}</h3>
            </div>
          </div>
          <div className="panel-empty">{t('common.loading')}</div>
        </div>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: '16px 8px',
          gap: '24px',
          height: '100%',
          border: 1,
          borderColor: 'divider',
          borderRadius: '8px',
          bgcolor: 'background.paper'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '20px' }}>{t('alerts.title')}</Typography>
        </Box>
        {[1, 2, 3].map((item) => (
          <Skeleton key={item} variant="rounded" height={96} sx={{ borderRadius: '8px' }} />
        ))}
      </Box>
    );
  }

  if (panelLayout) {
    return (
      <>
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title">
              <h3>{t('alerts.title')}</h3>
              <span className="badge">{totalOpen}</span>
            </div>
            <div className="panel-actions">
              <button
                type="button"
                className="btn filter-btn"
                onClick={() => setFilterOpen((open) => !open)}
              >
                <FilterList fontSize="small" />
                {t('alerts.filter')}
              </button>
              <button
                type="button"
                className="icon-btn"
                onClick={loadAlerts}
                aria-label={t('alerts.refresh')}
              >
                <Refresh fontSize="small" />
              </button>
            </div>
          </div>

          {filterOpen && (
            <FormControl size="small" sx={{ minWidth: 150, mb: 1 }}>
              <Select
                value={filter.severity}
                onChange={(event) => {
                  setFilter((prev) => ({ ...prev, severity: event.target.value }));
                  setFilterOpen(false);
                }}
                displayEmpty
              >
                <MenuItem value="">{t('alerts.all')}</MenuItem>
                <MenuItem value="critical">{t('alerts.criticalPlural')}</MenuItem>
                <MenuItem value="warning">{t('alerts.warningPlural')}</MenuItem>
              </Select>
            </FormControl>
          )}

          {error && (
            <MuiAlert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
              {error}
            </MuiAlert>
          )}

          {alerts.length === 0 ? (
            <div className="panel-empty">{t('alerts.noAlerts')}</div>
          ) : (
            <div className="alert-list">
              {alerts.map((alert) => {
                const config = severityConfig[alert.severity] || severityConfig.info;
                const badgeClass =
                  alert.severity === 'critical'
                    ? 'alert-badge critical'
                    : alert.severity === 'warning'
                      ? 'alert-badge warning'
                      : 'alert-badge';

                return (
                  <button
                    key={alert.alert_id}
                    type="button"
                    className="alert-item"
                    onClick={() => setSelectedAlert(alert)}
                  >
                    <span className={badgeClass}>{alert.occurrence_count || 1}</span>
                    <div className="alert-body">
                      <div className="alert-title">{alert.title}</div>
                      <div className="alert-meta">
                        {t('alerts.lastOccurrence')}: {formatTimeAgo(alert.last_occurrence)}
                      </div>
                    </div>
                    <MoreVert sx={{ width: 16, height: 16, color: config.color }} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {alertDialog}
      </>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: '16px 8px',
        gap: '24px',
        height: '100%',
        border: 1,
        borderColor: 'divider',
        borderRadius: '8px',
        bgcolor: 'background.paper',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: '20px',
                lineHeight: '150%',
                letterSpacing: '-0.01em',
                color: 'text.primary'
              }}
            >
              {t('alerts.title')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '24px',
                height: '24px',
                bgcolor: '#016FF4',
                borderRadius: '20px'
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '150%',
                  letterSpacing: '-0.01em',
                  color: '#FFFFFF'
                }}
              >
                {totalOpen}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box
              onClick={() => setFilterOpen(!filterOpen)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: '4px',
                height: '32px',
                border: '1px solid #016FF4',
                borderRadius: '8px',
                cursor: 'pointer',
                '&:hover': { bgcolor: isDark ? 'rgba(56, 188, 255, 0.08)' : 'rgba(1, 111, 244, 0.04)' }
              }}
            >
              <FilterList sx={{ width: 24, height: 24, color: '#016FF4' }} />
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  fontSize: '10px',
                  lineHeight: '10px',
                  letterSpacing: '-0.01em',
                  color: isDark ? 'primary.light' : '#001035',
                  mx: '4px'
                }}
              >
                {t('alerts.filter')}
              </Typography>
              <KeyboardArrowDown sx={{ width: 16, height: 16, color: '#016FF4' }} />
            </Box>
            <IconButton onClick={loadAlerts} sx={{ p: 0 }} aria-label={t('alerts.refresh')}>
              <Refresh sx={{ width: 24, height: 24, color: '#016FF4' }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '4px' }}>
          {criticalCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                px: '2px',
                py: '3px',
                bgcolor: '#DC5F5F',
                borderRadius: '8px'
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  fontSize: '10px',
                  lineHeight: '10px',
                  letterSpacing: '-0.01em',
                  color: '#FFFFFF',
                  px: '6px'
                }}
              >
                {getSeverityCountLabel('critical', criticalCount)}
              </Typography>
            </Box>
          )}
          {warningCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                px: '2px',
                py: '3px',
                bgcolor: '#E27D35',
                borderRadius: '8px'
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  fontSize: '10px',
                  lineHeight: '10px',
                  letterSpacing: '-0.01em',
                  color: '#FFFFFF',
                  px: '6px'
                }}
              >
                {getSeverityCountLabel('warning', warningCount)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {filterOpen && (
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={filter.severity}
            onChange={(event) => {
              setFilter((prev) => ({ ...prev, severity: event.target.value }));
              setFilterOpen(false);
            }}
            displayEmpty
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#016FF4'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#016FF4'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#016FF4'
              }
            }}
          >
            <MenuItem value="">{t('alerts.all')}</MenuItem>
            <MenuItem value="critical">{t('alerts.criticalPlural')}</MenuItem>
            <MenuItem value="warning">{t('alerts.warningPlural')}</MenuItem>
          </Select>
        </FormControl>
      )}

      {error && (
        <MuiAlert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </MuiAlert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: '6px',
          flex: 1,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 0.5,
            '&::-webkit-scrollbar': {
              width: '4px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? 'rgba(255, 255, 255, 0.2)' : '#D0D0D0',
              borderRadius: '2px'
            }
          }}
        >
          {alerts.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  color: 'text.secondary'
                }}
              >
                {t('alerts.noAlerts')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '12px',
                  color: 'text.secondary',
                  textAlign: 'center',
                  maxWidth: '240px'
                }}
              >
                {t('alerts.noAlertsDescription')}
              </Typography>
            </Box>
          ) : (
            alerts.map((alert) => {
              const config = severityConfig[alert.severity] || severityConfig.info;

              return (
                <Box
                  key={alert.alert_id}
                  onClick={() => setSelectedAlert(alert)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: '16px 8px',
                    gap: '8px',
                    minHeight: '96px',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:hover': {
                      borderColor: 'rgba(1, 111, 244, 0.3)',
                      bgcolor: isDark ? 'rgba(56, 188, 255, 0.06)' : 'rgba(1, 111, 244, 0.02)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: 1
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '24px',
                        height: '24px',
                        bgcolor: config.color,
                        borderRadius: '20px',
                        flexShrink: 0
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Inter', sans-serif",
                          fontWeight: 600,
                          fontSize: '12px',
                          lineHeight: '150%',
                          letterSpacing: '-0.01em',
                          color: '#FFFFFF'
                        }}
                      >
                        {alert.occurrence_count || 1}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Inter', sans-serif",
                          fontWeight: 600,
                          fontSize: '16px',
                          lineHeight: '150%',
                          letterSpacing: '-0.01em',
                          color: 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {alert.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Inter', sans-serif",
                          fontWeight: 300,
                          fontSize: '10px',
                          lineHeight: '10px',
                          letterSpacing: '-0.01em',
                          color: 'text.secondary',
                          mt: '4px'
                        }}
                      >
                        {t('alerts.lastOccurrence')}: {formatTimeAgo(alert.last_occurrence)}
                      </Typography>
                    </Box>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedAlert(alert);
                    }}
                    sx={{ p: '6px 3px' }}
                  >
                    <MoreVert sx={{ width: 16, height: 12, color: '#016FF4' }} />
                  </IconButton>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      {alertDialog}
    </Box>
  );
};

export default AlertCenter;
